import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import { AI_PROMPT_TARGET_OBJECTS } from 'constants/aiPrompts';
import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import { Summary } from 'models/flexible/Summary';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexSummaries, adminShowSummary, adminSummariesQueryKeys } from 'services/api/admin/summaries';
import type { AdminSummaryShowFields, AdminSummaryShowInclude } from 'services/api/fields';
import { colors } from 'styles/theme/colors';
import { BodySmallBold } from 'styles/typography';

import SummaryDetailSlideout from './SummaryDetailSlideout';

const INCLUDES = [] as const;

function buildConfig(
  onView: (row: Summary<AdminSummaryShowFields<(typeof INCLUDES)[number]>>) => void
): AdminListPageConfig<Summary<AdminSummaryShowFields<(typeof INCLUDES)[number]>>> {
  return {
    title: 'Generated Summaries',
    columnStorageKey: 'admin.summaries',
    queryKey: adminSummariesQueryKeys.index(),
    fetchFn: (params, signal) => adminIndexSummaries(params, signal, [...INCLUDES]),
    includes: INCLUDES,
    fetchParams: {
      defaultSort: 'createdAt desc',
    },
    searchPlaceholder: 'Search Summaries',
    showResultCount: true,

    filters: [
      {
        key: 'target_classname',
        label: 'Target Object',
        type: 'dropdown',
        staticOptions: AI_PROMPT_TARGET_OBJECTS,
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
      },
      {
        key: 'client',
        label: 'Client',
        type: 'dropdown',
        fetchConfig: {
          queryKey: ['adminClients', 'filter'],
          fetchFn: adminIndexClients,
          params: { sortBy: 'name asc' },
        },
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
      },
      {
        key: 'created_at',
        label: 'Date Range',
        type: 'date_range',
        defaultRange: 90,
        toRequest: (value: any) => {
          if (!value?.start && !value?.end) return undefined;
          const startStr = value.start ? value.start.toISOString().split('T')[0] : '';
          const endStr = value.end ? value.end.toISOString().split('T')[0] : '';
          return `${startStr},${endStr}`;
        },
      },
      {
        key: 'clientType.not',
        label: 'Include Olio Admins',
        type: 'checkbox',
        toRequest: (value) => (value ? undefined : 'Admin'),
      },
      {
        key: 'status.not',
        label: 'Include Errored',
        type: 'checkbox',
        toRequest: (value) => (value ? undefined : ['error']),
      },
    ],

    columns: [
      {
        id: 'summary',
        header: 'Summary',
        render: (row) => {
          const hasError = row.status === 'error' || !!row.error;
          return (
            <SummaryLink onClick={() => onView(row)}>
              {hasError && <ErrorDot title='Error' />}
              {row.title ? `${row.title} Summary` : '—'}
            </SummaryLink>
          );
        },
      },
      {
        id: 'targetClassname',
        header: 'Target Object',
        render: (row) => row.targetClassname || '—',
      },
      {
        id: 'patientName',
        header: 'Patient Name',
        render: (row) => row.patientName || '—',
      },
      {
        id: 'client',
        header: 'Client',
        render: (row) => <BodySmallBold>{row.client?.name || '—'}</BodySmallBold>,
      },
      {
        id: 'user',
        header: 'User',
        render: (row) => row.user?.name || '—',
      },
      {
        id: 'sourcesCount',
        header: '# of Sources',
        sortable: false,
        render: (row) => row.sourcesCount ?? '—',
      },
      {
        id: 'createdAt',
        header: 'Created At',
        render: (row) => (row.createdAt ? simpleDateAtTime(row.createdAt) : '—'),
      },
    ],
  };
}

export default function Summaries() {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const config = useMemo(() => buildConfig((row) => setSelectedId(row.id)), []);
  const state = useAdminListPage(config);

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: adminSummariesQueryKeys.show(selectedId ?? ''),
    queryFn: ({ signal }) => adminShowSummary(selectedId!, signal, SHOW_INCLUDES),
    enabled: !!selectedId,
  });

  return (
    <>
      <AdminListPageRenderer config={config} state={state} />
      <SummaryDetailSlideout
        show={!!selectedId}
        summary={detail ?? null}
        loading={!!selectedId && detailLoading}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}

const SHOW_INCLUDES = [
  'attachments',
  'attachments.blob',
  'resultPayload',
  'aiArtifact',
  'patientDemographics',
  'aiPrompt',
] as const satisfies readonly AdminSummaryShowInclude[];

const SummaryLink = styled.span`
  color: ${colors.blue100};
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${colors.red100};
  flex-shrink: 0;
`;
